/**
 * Turns a song into timed notes: the pure clock of the music (no Web Audio, unit-tested).
 *
 * Driven by a lookahead scheduler: a timer wakes up every few tens of milliseconds and asks
 * for every note that starts before "now + lookahead"; the notes are then scheduled at
 * their exact times on the AudioContext clock, so timer jitter never shows in the rhythm.
 * Steps that are already late when the timer finally runs (a long frame, a paused tab) are
 * skipped silently instead of being played in a burst.
 *
 * Two ways to keep time:
 *  - on its own (`start`): the time scale (turbo, slow motion) glides to its new value over
 *    ~TEMPO_GLIDE seconds, so the tempo speeds up or slows down smoothly;
 *  - on the game's clock (`sync`): the engine says where in the song the music should be at
 *    a moment (the attempt's game time) and how fast that moves. The tempo then follows game
 *    time exactly, turbo and slow motion included, and small drifts are pulled in gently:
 *    the beat stays where the level's obstacles are. A new tempo or a big drift moves the
 *    next step to its place at once, but a step that already played is never played again
 *    (its notes are scheduled already: a repeat would sound as a stutter).
 */
import { type Song, type SongEvent, STEPS_PER_BAR } from './composer';

/** The music scheduler wakes up this often, ms (well below the lookahead)... */
export const SCHEDULER_TICK_MS = 25;
/** ...and plans this far ahead on the AudioContext clock, s. */
export const SCHEDULER_LOOKAHEAD = 0.12;
/** Time constant of the tempo glide, s. */
export const TEMPO_GLIDE = 0.3;
/** A step starting more than this before "now" is skipped, s. */
export const MAX_LATE = 0.05;
/** Off the game's clock by less than this, the music is left alone (jitter), s. */
export const SYNC_TOLERANCE = 0.002;
/** Off by more than this, it jumps to the right place at once; in between it is pulled in, s. */
export const SYNC_SNAP = 0.06;
/** Share of a small drift taken back per sync (the engine syncs every frame). */
const SYNC_PULL = 0.3;
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;

export type EmitNote = (event: SongEvent, time: number, stepSeconds: number, scale: number) => void;

function clampScale(scale: number): number {
  return Number.isFinite(scale) ? Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale)) : 1;
}

export class Sequencer {
  private step = 0;
  private cursor = 0;
  private nextTime = 0;
  private scale = 1;
  private target = 1;
  private running = false;
  private loops = 0;
  /** On the game's clock (sync) rather than on its own. */
  private synced = false;
  /** The last step played in time (loops × song length + step); -1: none yet. */
  private played = -1;

  constructor(
    private readonly song: Song,
    private readonly bpm: number,
  ) {}

  /** Seconds per sixteenth note at time scale 1. */
  get baseStepSeconds(): number {
    return 60 / this.bpm / 4;
  }

  /** Length of one loop at time scale 1, s. */
  get loopSeconds(): number {
    return this.song.length * this.baseStepSeconds;
  }

  /** Seconds per bar at time scale 1. */
  get barSeconds(): number {
    return STEPS_PER_BAR * this.baseStepSeconds;
  }

  /** Current (gliding) time scale. */
  get timeScale(): number {
    return this.scale;
  }

  get position(): { step: number; loops: number; nextTime: number } {
    return { step: this.step, loops: this.loops, nextTime: this.nextTime };
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Whether the engine's clock leads (see sync). */
  get isSynced(): boolean {
    return this.synced;
  }

  /** Starts from the first step at `time`. */
  start(time: number): void {
    this.step = 0;
    this.cursor = 0;
    this.loops = 0;
    this.played = -1;
    this.nextTime = time;
    this.running = true;
  }

  /** New time scale; glides there unless `immediate` (a synced sequencer takes the sync's). */
  setTimeScale(scale: number, immediate = false): void {
    const clamped = clampScale(scale);
    this.target = clamped;
    if (immediate) this.scale = clamped;
  }

  /**
   * Puts the music on the game's clock: at `time` (AudioContext seconds) the song should be
   * `position` seconds in (at its base tempo; negative: it begins that much later), moving at
   * `scale`. The first call, a new scale and any drift beyond SYNC_SNAP place it there at
   * once; small drifts are taken back a bit with every call. Starts a sequencer that was not
   * running.
   */
  sync(time: number, position: number, scale: number): void {
    const s = clampScale(scale);
    const rescaled = s !== this.scale;
    this.scale = s;
    this.target = s;
    const base = this.baseStepSeconds;
    const absolute = this.loops * this.song.length + this.step;
    const due = time + (absolute * base - position) / s;
    const drift = due - this.nextTime;
    if (this.synced && this.running && !rescaled && Math.abs(drift) <= SYNC_SNAP) {
      if (Math.abs(drift) > SYNC_TOLERANCE) this.nextTime += drift * SYNC_PULL;
      return;
    }
    // Jump: the first step at or after the position (one that is only a little late still
    // plays, as at any start), but never one that already played: after a new tempo the
    // steps scheduled ahead stay, and the next one moves.
    const first = Math.max(0, Math.ceil((position - MAX_LATE * s) / base - 1e-9), this.played + 1);
    this.loops = Math.floor(first / this.song.length);
    this.step = first - this.loops * this.song.length;
    this.cursor = this.firstEventAt(this.step);
    this.nextTime = time + (first * base - position) / s;
    this.running = true;
    this.synced = true;
  }

  pause(): void {
    this.running = false;
  }

  /** Continues with the next step at `time`. */
  resume(time: number): void {
    this.nextTime = time;
    this.running = true;
  }

  /**
   * Emits every note whose step starts before `until`. Steps that start more than MAX_LATE
   * before `now` are skipped (their notes are not emitted).
   */
  advance(until: number, now: number, emit: EmitNote): void {
    if (!this.running) return;
    const events = this.song.events;
    while (this.nextTime < until) {
      const stepSeconds = this.baseStepSeconds / this.scale;
      const late = this.nextTime < now - MAX_LATE;
      if (!late) this.played = this.loops * this.song.length + this.step;
      while (this.cursor < events.length) {
        const event = events[this.cursor];
        if (!event || event.step !== this.step) break;
        if (!late) emit(event, this.nextTime, stepSeconds, this.scale);
        this.cursor++;
      }
      this.nextTime += stepSeconds;
      if (!this.synced) {
        this.scale += (this.target - this.scale) * (1 - Math.exp(-stepSeconds / TEMPO_GLIDE));
        if (Math.abs(this.target - this.scale) < 1e-4) this.scale = this.target;
      }
      this.step++;
      if (this.step >= this.song.length) {
        this.step = 0;
        this.cursor = 0;
        this.loops++;
      }
    }
  }

  /** Index of the first event at or after `step` (events are sorted by step). */
  private firstEventAt(step: number): number {
    const events = this.song.events;
    let lo = 0;
    let hi = events.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((events[mid]?.step ?? 0) < step) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
}
